import { Box, Typography } from "@mui/material";
import TypoBold from "./TypoBold";
import { useTranslation } from "react-i18next";

const FORM_URL =
  "https://docs.dingtalk.com/notable/share/form/v01Yvenve52dMPGAloy_WUrWjbK_tIun0mk?source=link";

export default function PartnerSection() {
  const [t] = useTranslation("global");

  return (
    <Box sx={{ py: 5 }}>
      {/* ========== Partner Intro ========== */}
      <Box
        textAlign="center"
        sx={{ maxWidth: 900, mx: "auto", mb: 6 }}
      >
        <Typography
          variant="h4"
          fontWeight={700}
          sx={{
            fontSize: { xs: "24px", sm: "32px" },
            color: "#074478",
            mb: 1,
          }}
        >
          {t("PickUp.partner_title")}
        </Typography>
        <Typography
          fontWeight={500}
          sx={{
            fontSize: { xs: "16px", sm: "20px" },
            color: "#0081ed",
            mb: 2,
          }}
        >
          {t("PickUp.partner_subtitle")}
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: "14px", sm: "18px" },
            lineHeight: 1.6,
            color: "rgba(0,0,0,0.6)",
          }}
        >
          {t("PickUp.partner_desc")}
        </Typography>
      </Box>

      {/* ========== Why Partner With Us ========== */}
      <TypoBold textAlign="center" sx={{ mb: 4 }}>
        {t("PickUp.why_partner_title")}
      </TypoBold>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          flexWrap: "wrap",
          gap: 5,
          mb: 6,
        }}
      >
        {[
          { titleKey: "wp_card1_title", descKey: "wp_card1_desc" },
          { titleKey: "wp_card2_title", descKey: "wp_card2_desc" },
          { titleKey: "wp_card3_title", descKey: "wp_card3_desc" },
          { titleKey: "wp_card4_title", descKey: "wp_card4_desc" },
        ].map((card, idx) => (
          <WhyPartnerCard key={idx} titleKey={card.titleKey} descKey={card.descKey} />
        ))}
      </Box>

      {/* ========== How It Works ========== */}
      <Box
        sx={{
          backgroundColor: "#f5f5f5",
          borderRadius: "16px",
          p: { xs: 3, sm: 6 },
          mb: 6,
        }}
      >
        <Typography
          variant="h4"
          fontWeight={700}
          textAlign="center"
          sx={{
            fontSize: { xs: "22px", sm: "28px" },
            color: "#074478",
            mb: 4,
          }}
        >
          {t("PickUp.hiw_title")}
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 3,
            flexWrap: "wrap",
          }}
        >
          {[
            { num: 1, titleKey: "hiw_step1_title", descKey: "hiw_step1_desc" },
            { num: 2, titleKey: "hiw_step2_title", descKey: "hiw_step2_desc" },
            { num: 3, titleKey: "hiw_step3_title", descKey: "hiw_step3_desc" },
          ].map((step, idx) => (
            <StepCard key={idx} {...step} />
          ))}
        </Box>
      </Box>

      {/* ========== CTA ========== */}
      <Box
        sx={{
          backgroundColor: "#113B7C",
          borderRadius: "16px",
          p: { xs: 4, sm: 7 },
          textAlign: "center",
          color: "#fff",
        }}
      >
        <Typography
          variant="h4"
          fontWeight={700}
          sx={{
            fontSize: { xs: "24px", sm: "32px" },
            color: "#fff",
            mb: 2,
          }}
        >
          {t("PickUp.partner_cta_title")}
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: "14px", sm: "18px" },
            color: "rgba(255,255,255,0.85)",
            maxWidth: 700,
            mx: "auto",
            mb: 4,
            lineHeight: 1.6,
          }}
        >
          {t("PickUp.partner_cta_desc")}
        </Typography>
        <Box
          component="a"
          href={FORM_URL}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#bff542",
            color: "#074478",
            fontFamily: "'Roboto', sans-serif",
            fontSize: "16px",
            fontWeight: 700,
            px: 4,
            py: 1.75,
            borderRadius: "8px",
            textDecoration: "none",
            transition: "background-color 0.2s, transform 0.2s",
            "&:hover": {
              backgroundColor: "#d4ff70",
              transform: "translateY(-2px)",
            },
          }}
        >
          {t("PickUp.partner_cta_btn")}
        </Box>
        <Typography
          sx={{
            mt: 2,
            fontSize: "14px",
            color: "rgba(255,255,255,0.7)",
            fontStyle: "italic",
          }}
        >
          {t("PickUp.partner_cta_time")}
        </Typography>
      </Box>
    </Box>
  );
}

/* ========== Why Partner Card ========== */
function WhyPartnerCard({ titleKey, descKey }) {
  const [t] = useTranslation("global");
  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        border: "1px solid rgba(0,0,0,0.08)",
        borderRadius: "8px",
        p: 2,
        width: { xs: "100%", sm: "45%", md: "220px" },
        transition: "box-shadow 0.2s, transform 0.2s",
        "&:hover": {
          boxShadow:
            "0 2px 4px -1px rgba(0,0,0,0.2), 0 4px 5px 0 rgba(0,0,0,0.14), 0 1px 10px 0 rgba(0,0,0,0.12)",
          transform: "translateY(-2px)",
        },
      }}
    >
      <Typography
        fontWeight={700}
        sx={{ fontSize: "18px", color: "#074478", mb: 1 }}
      >
        {t(`PickUp.${titleKey}`)}
      </Typography>
      <Typography
        sx={{ fontSize: "15px", lineHeight: 1.5, color: "rgba(0,0,0,0.6)" }}
      >
        {t(`PickUp.${descKey}`)}
      </Typography>
    </Box>
  );
}

/* ========== Step Card ========== */
function StepCard({ num, titleKey, descKey }) {
  const [t] = useTranslation("global");
  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        borderRadius: "12px",
        p: 3.5,
        textAlign: "center",
        flex: "1 1 0",
        minWidth: { xs: "100%", sm: 240 },
        maxWidth: { xs: "100%", sm: 320 },
        boxShadow:
          "0 2px 1px -1px rgba(0,0,0,0.2), 0 1px 1px 0 rgba(0,0,0,0.14), 0 1px 3px 0 rgba(0,0,0,0.12)",
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          backgroundColor: "#0081ed",
          color: "#fff",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "20px",
          fontWeight: 700,
          mx: "auto",
          mb: 2,
        }}
      >
        {num}
      </Box>
      <Typography
        fontWeight={700}
        sx={{ fontSize: "18px", color: "#074478", mb: 1 }}
      >
        {t(`PickUp.${titleKey}`)}
      </Typography>
      <Typography sx={{ fontSize: "15px", color: "rgba(0,0,0,0.6)" }}>
        {t(`PickUp.${descKey}`)}
      </Typography>
    </Box>
  );
}